import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';

// Auth
import { useAuth, AuthProvider } from './hooks/useAuth';

// Layout
import { AdminLayout } from './components/layout/AdminLayout';

// Pages
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { OrdersPage, OrderDetailsPage } from './pages/orders';
import { ShippingPage } from './pages/shipping/ShippingPage';
import { InvoicesPage } from './pages/invoices/InvoicesPage';
import { CustomersPage } from './pages/customers/CustomersPage';
import { CustomerDetailsPage } from './pages/customers/CustomerDetailsPage';
import { ProductsPage } from './pages/products/ProductsPage';
import { ProductDetailsPage } from './pages/products/ProductDetailsPage';
import { PaymentsPage } from './pages/payments';
import { ReportsPage } from './pages/reports/ReportsPage';
import { MonitoringPage } from './pages/monitoring/MonitoringPage';
import { SettingsPage } from './pages/settings/SettingsPage';

// Common Components
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { LoadingScreen } from './components/common/LoadingScreen';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
      staleTime: 30 * 1000, // 30 seconds
      refetchOnMount: true,
      gcTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();

  if (state.loading) {
    return <LoadingScreen fullScreen />;
  }

  if (!state.isAuthenticated) {
    return <Navigate to="/login/" />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
}

// Redirect component for dynamic paths
function RedirectWithId() {
  const { pathname } = useLocation();
  const id = pathname.split('/').pop();
  const basePath = pathname.split('/').slice(0, -1).join('/');
  return <Navigate to={`${basePath}/${id}/`} replace />;
}

function AdminApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <React.Suspense fallback={<LoadingScreen fullScreen />}>
            <Routes>
              {/* Redirect routes for URLs without trailing slash */}
              <Route path="" element={<Navigate to="/" replace />} />
              <Route path="login" element={<Navigate to="/login/" replace />} />
              <Route path="orders" element={<Navigate to="/orders/" replace />} />
              <Route path="orders/:id" element={<RedirectWithId />} />
              <Route path="shipping" element={<Navigate to="/shipping/" replace />} />
              <Route path="invoices" element={<Navigate to="/invoices/" replace />} />
              <Route path="customers" element={<Navigate to="/customers/" replace />} />
              <Route path="customers/:id" element={<RedirectWithId />} />
              <Route path="products" element={<Navigate to="/products/" replace />} />
              <Route path="products/:id" element={<RedirectWithId />} />
              <Route path="payments" element={<Navigate to="/payments/" replace />} />
              <Route path="reports" element={<Navigate to="/reports/" replace />} />
              <Route path="monitoring" element={<Navigate to="/monitoring/" replace />} />
              <Route path="settings" element={<Navigate to="/settings/" replace />} />
              
              {/* Main routes with trailing slashes */}
              <Route path="login/" element={<LoginPage />} />
              
              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <AdminLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="orders/" element={<OrdersPage />} />
                <Route path="orders/:id/" element={<OrderDetailsPage />} />
                <Route path="shipping/" element={<ShippingPage />} />
                <Route path="invoices/" element={<InvoicesPage />} />
                <Route path="customers/" element={<CustomersPage />} />
                <Route path="customers/:id/" element={<CustomerDetailsPage />} />
                <Route path="products/" element={<ProductsPage />} />
                <Route path="products/:id/" element={<ProductDetailsPage />} />
                <Route path="payments/" element={<PaymentsPage />} />
                <Route path="reports/" element={<ReportsPage />} />
                <Route path="monitoring/" element={<MonitoringPage />} />
                <Route path="settings/" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </React.Suspense>
        </BrowserRouter>
      </AuthProvider>

      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster position="top-right" />
    </QueryClientProvider>
  );
}

export default AdminApp;
